<script>
    import { browser } from '$app/environment';
    import { getContext } from 'svelte';
    import Device from '$lib/utils/functions/device';

    let alreadySubscribedToPush; // Has OneSignal Sub (Boolean, undefined if no push support)
    let browserPermission; // 'denied', 'granted', 'default', undefined
    let oneSignalAPIReady = false; // Need to wait for api before prompt
    let notNow = true; // Cookie to remind user later; default to true until init/cookie read.

    const context = getContext('ctx');
    context.fussyDependanciesReady.oneSignalAPIReady.subscribe((ready) => {
        if (ready) oneSignalAPIReady = ready;
    });

    /**
     * Listens for changes in the permission status and triggers the permissionChangeListener callback.
     *
     * @param {string} permission - The current permission status.
     */
    function permissionChangeListener(permission) {
        browserPermission = Notification?.permission; // Browser Native
        if (permission) {
            window.OneSignal.User.PushSubscription.optIn(); // Resub on enable
            alreadySubscribedToPush = window.OneSignal.User.PushSubscription.optedIn; // or just hardcode to true?
        }
    }

    /**
     * This code block initializes the OneSignal web push notification service and handles permission changes.
     * It checks if push notifications are supported by the browser, retrieves the site's permission,
     * checks if the user is already subscribed to push notifications, and adds an event listener for permission changes.
     */
    $: if (browser && oneSignalAPIReady) {
        window.OneSignalDeferred.push(async (OneSignal) => {
            const isPushSupported = OneSignal.Notifications.isPushSupported();
            if (isPushSupported) {
                const iosPWA = Device.isiOSPWA(window);
                browserPermission = Notification?.permission; // Site's permission, not OS.
                alreadySubscribedToPush = !iosPWA ? OneSignal?.User?.PushSubscription?.optedIn : Boolean(OneSignal?.User?.PushSubscription?.token);
                notNow = document.cookie.includes('web_push_ignore=true');
                OneSignal.Notifications.addEventListener('permissionChange', permissionChangeListener); // Handle permission changes
            }
        });
    }

    /**
     * Function to prompt the user for native push notifications permission.
     * This function uses the OneSignal library to request permission.
     */
    const nativePushPrompt = () => {
        window.OneSignalDeferred.push(async function (OneSignal) {
            OneSignal.Notifications.requestPermission();
        });
    };

    /**
     * Function to set a cookie and display an alert.
     * @param {number} timeout - The timeout value for the cookie in seconds. Default is 3 days.
     */
    const remindMeLater = (timeout = 259200) => {
        document.cookie = `web_push_ignore=${true}; max-age=${timeout}; path=/; secure; samesite=strict;`;
    };
</script>

{#if browser && typeof alreadySubscribedToPush !== 'undefined'}
    {#if !alreadySubscribedToPush && !notNow && typeof browserPermission !== 'undefined'}
        <slot {nativePushPrompt} {browserPermission} {remindMeLater} />
    {:else if alreadySubscribedToPush}
        <slot name="subscribed" />
    {/if}
{/if}

<!-- USAGE EXAMPLE -->
<!--

<WebPushPrompt let:nativePushPrompt let:browserPermission>
    <div>
        Subscribe to notifications to keep up to date with tracking
        {#if browserPermission !== 'denied'}
            <button on:click={nativePushPrompt}>Subscribe</button>
        {:else}
            re-enable notifications to subscribe.
        {/if}
    </div>
    <div slot="subscribed">You're already subscribed... TODO: Unsubscribe?</div>
</WebPushPrompt>



-->
